import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { BrandMark } from "../components/BrandMark";
import { PublicMobileMenu } from "../components/PublicMobileMenu";
import { OFFICE_HOURS_LINES } from "../lib/office-hours";
import { toast } from "sonner";
import {
    Menu, X, ArrowRight, MapPin, Phone, Mail, Info,
    Sparkles, GraduationCap, HeartHandshake, Laptop,
    Users, Headphones, Building2, Calculator,
    FileText, ClipboardCheck, MessageSquare, CheckCircle2,
    BriefcaseBusiness, Facebook, Instagram, Linkedin, MessageCircle, Clock,
} from "lucide-react";

/* ---------- nav ---------- */
const NAV = [
    { label: "Home", to: "/home" },
    { label: "About Us", to: "/about-us" },
    { label: "Loans", to: "/loans" },
    { label: "Saving Scheme", to: "/daily-savings" },
    { label: "Investment", to: "/investment" },
    { label: "Careers", to: "/careers" },
    { label: "Contact Us", to: "/contact-us" },
];

const WHY_JOIN = [
    { icon: HeartHandshake, title: "Meaningful field impact",
        desc: "Work close to communities and support real financial progress on the ground." },
    { icon: GraduationCap, title: "Learning and growth",
        desc: "Structured learning paths, mentoring and on-the-job guidance across roles." },
    { icon: Sparkles, title: "Responsible work culture",
        desc: "A culture built around transparency, respect and considered decision-making." },
    { icon: Laptop, title: "Digital-first operations",
        desc: "Modern tools, digital records and clean processes across day-to-day work." },
];

const ROLE_AREAS = [
    { icon: Users, title: "Field Collection",
        desc: "Serve customers on the ground and manage day-to-day collections responsibly." },
    { icon: Headphones, title: "Customer Service",
        desc: "Support customer queries with clarity, empathy and accurate information." },
    { icon: Building2, title: "Branch Operations",
        desc: "Keep branch processes running smoothly, from documentation to daily reports." },
    { icon: Calculator, title: "Accounts & Administration",
        desc: "Maintain organised records, reconciliations and administrative support." },
];

const HIRING = [
    { icon: FileText, title: "Application" },
    { icon: ClipboardCheck, title: "Review" },
    { icon: MessageSquare, title: "Interview" },
    { icon: CheckCircle2, title: "Selection" },
];

const FOOTER_LINKS = {
    Products: ["Personal Loan", "Business Loan", "Group Loan", "Saving Scheme", "Investment"],
    Company: ["About Us", "Careers", "News & Updates", "Contact Us"],
    Support: ["FAQs", "Grievance Redressal", "Customer Care", "Downloads"],
};

/* placeholders — real values to be published later */
const BRANCH_ADDRESS = "Sai Sanskar Apartment, Kachare Galli, Dange Chauk Road, Ishwarpur, 415409";
const PHONE = "+91 93592 86008";
const CAREERS_EMAIL = "careers@lokdhara.co";
const MAPS_LINK = "https://maps.app.goo.gl/98yYfGcodyHcUgk78";

const placeholderLoginToast = () => {
    window.location.assign("/customer-login");
};

/* ---------- page ---------- */
export default function PublicCareers() {
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        document.title = "Careers | Lokdhara Microfinance";
        window.scrollTo({ top: 0, behavior: "auto" });
    }, []);

    const go = (to) => { if (to) navigate(to); };

    return (
        <div id="top" className="min-h-screen bg-white text-slate-900 antialiased">
            {/* HEADER ---------------------------------------------------- */}
            <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between gap-4">
                    <Link to="/home" className="flex items-center" data-testid="car-brand">
                        <BrandMark size={56} className="sm:!h-14" />
                    </Link>
                    <nav className="hidden lg:flex items-center gap-6 text-sm font-medium text-slate-700">
                        {NAV.map((n) => (
                            <button key={n.label}
                                onClick={() => go(n.to)}
                                className={`hover:text-brand-royal transition ${n.to === "/careers" ? "text-brand-royal" : ""}`}
                                data-testid={`car-nav-${n.label.replace(/\s+/g, "-").toLowerCase()}`}
                            >
                                {n.label}
                            </button>
                        ))}
                    </nav>
                    <div className="flex items-center gap-2">
                        <Button
                            className="hidden sm:inline-flex bg-brand-royal hover:bg-brand-hover text-white rounded-full h-10 px-5"
                            onClick={placeholderLoginToast}
                            data-testid="car-header-customer-login"
                        >
                            Customer Login
                        </Button>
                        <button
                            aria-label="Open menu"
                            className="lg:hidden h-10 w-10 rounded-lg border border-slate-200 grid place-items-center"
                            onClick={() => setMenuOpen(true)}
                            data-testid="car-mobile-menu-btn"
                        >
                            <Menu className="h-5 w-5" />
                        </button>
                    </div>
                </div>
                {menuOpen && (
                    <PublicMobileMenu
                        open={menuOpen}
                        onOpenChange={setMenuOpen}
                        nav={NAV}
                        onNavItemClick={(n) => go(n.to)}
                        onLoginClick={placeholderLoginToast}
                        testId="car-mobile-menu"
                        activePath="/careers"
                    />
                )}
            </header>

            {/* NAVY HERO ------------------------------------------------ */}
            <section className="relative overflow-hidden bg-brand-navy text-white">
                <div className="absolute inset-0 pointer-events-none opacity-20"
                    style={{ backgroundImage: "radial-gradient(circle at 15% 25%, rgba(255,255,255,0.15) 0%, transparent 45%), radial-gradient(circle at 85% 65%, rgba(255,255,255,0.10) 0%, transparent 40%)" }}
                    aria-hidden
                />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
                    <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-tint bg-white/10 rounded-full px-3 py-1">
                        Careers
                    </span>
                    <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight max-w-3xl">
                        Build Your Career with Lokdhara
                    </h1>
                    <p className="mt-5 text-base sm:text-lg text-slate-200/90 max-w-2xl leading-relaxed">
                        Join a team that works close to communities — with structured processes,
                        modern tools and a culture built on responsibility.
                    </p>
                </div>
            </section>

            {/* WHY JOIN US --------------------------------------------- */}
            <section className="py-20 sm:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-royal bg-brand-soft rounded-full px-3 py-1">
                            Why Join Us
                        </span>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-brand-navy tracking-tight">
                            What working with us looks like
                        </h2>
                    </div>
                    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        {WHY_JOIN.map(({ icon: Icon, title, desc }) => (
                            <Card key={title}
                                className="rounded-2xl border-slate-100 p-7 hover:shadow-card-hover transition"
                                data-testid={`car-why-${title.replace(/\s+/g, "-").toLowerCase()}`}
                            >
                                <div className="h-12 w-12 rounded-xl bg-brand-soft grid place-items-center">
                                    <Icon className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                                </div>
                                <h3 className="mt-5 font-semibold text-slate-900 text-base">{title}</h3>
                                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{desc}</p>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* ROLE AREAS ---------------------------------------------- */}
            <section className="bg-slate-50 py-20 sm:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-royal">
                            Role Areas
                        </span>
                        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-brand-navy tracking-tight">
                            Where you can contribute
                        </h2>
                    </div>
                    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        {ROLE_AREAS.map(({ icon: Icon, title, desc }) => (
                            <Card key={title}
                                className="rounded-2xl border-slate-100 bg-white p-7 hover:shadow-card-hover transition"
                                data-testid={`car-role-${title.replace(/\s+/g, "-").toLowerCase()}`}
                            >
                                <div className="h-12 w-12 rounded-xl bg-brand-tint grid place-items-center">
                                    <Icon className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                                </div>
                                <h3 className="mt-5 font-semibold text-slate-900 text-base">{title}</h3>
                                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{desc}</p>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* HIRING PROCESS ------------------------------------------ */}
            <section className="py-20 sm:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-royal bg-brand-soft rounded-full px-3 py-1">
                            Hiring Process
                        </span>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-brand-navy tracking-tight">
                            A simple four-step journey
                        </h2>
                    </div>
                    <div className="grid gap-6 sm:gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {HIRING.map(({ icon: Icon, title }, i) => (
                            <div key={title}
                                className="relative flex lg:flex-col items-center lg:items-start gap-4 lg:gap-5 bg-white rounded-2xl p-5 border border-slate-100"
                                data-testid={`car-hiring-step-${i + 1}`}
                            >
                                <div className="relative h-14 w-14 rounded-2xl bg-brand-navy grid place-items-center shrink-0">
                                    <Icon className="h-6 w-6 text-white" strokeWidth={1.75} />
                                    <span className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-brand-royal text-white text-[11px] font-bold grid place-items-center">
                                        {i + 1}
                                    </span>
                                </div>
                                <div className="font-semibold text-slate-900 leading-snug">{title}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* INTERESTED CANDIDATES ---------------------------------- */}
            <section className="bg-brand-soft py-20 sm:py-24">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <div className="text-center max-w-2xl mx-auto mb-12">
                        <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-royal">
                            Interested Candidates
                        </span>
                        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-brand-navy tracking-tight leading-tight">
                            Reach out to us
                        </h2>
                        <p className="mt-5 text-slate-700 leading-relaxed">
                            Interested candidates may visit our branch, call us, or email their CV.
                            Complete job information will be provided. After receiving your call or
                            CV, our team will contact you.
                        </p>
                    </div>

                    <div className="grid gap-5 md:grid-cols-3">
                        <Card className="rounded-2xl border-white bg-white/95 p-6 flex flex-col"
                            data-testid="car-contact-address"
                        >
                            <div className="h-11 w-11 rounded-xl bg-brand-soft grid place-items-center">
                                <MapPin className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                            </div>
                            <div className="mt-4 text-xs uppercase tracking-wider font-semibold text-slate-500">
                                Branch Address
                            </div>
                            <div className="mt-1 text-slate-800 font-medium">{BRANCH_ADDRESS}</div>
                            <a
                                href={MAPS_LINK}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-5"
                                data-testid="car-visit-branch-link"
                            >
                                <Button className="w-full rounded-full h-11 bg-brand-navy hover:bg-brand-royal text-white"
                                    data-testid="car-visit-branch-btn"
                                >
                                    Visit Branch <ArrowRight className="h-4 w-4 ml-2" />
                                </Button>
                            </a>
                        </Card>

                        <Card className="rounded-2xl border-white bg-white/95 p-6 flex flex-col"
                            data-testid="car-contact-phone"
                        >
                            <div className="h-11 w-11 rounded-xl bg-brand-soft grid place-items-center">
                                <Phone className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                            </div>
                            <div className="mt-4 text-xs uppercase tracking-wider font-semibold text-slate-500">
                                Phone Number
                            </div>
                            <div className="mt-1 text-slate-800 font-medium">{PHONE}</div>
                            <a
                                href={`tel:${PHONE.replace(/\s+/g, "")}`}
                                className="mt-5"
                                data-testid="car-call-us-link"
                            >
                                <Button className="w-full rounded-full h-11 bg-brand-navy hover:bg-brand-royal text-white">
                                    Call Us <ArrowRight className="h-4 w-4 ml-2" />
                                </Button>
                            </a>
                        </Card>

                        <Card className="rounded-2xl border-white bg-white/95 p-6 flex flex-col"
                            data-testid="car-contact-email"
                        >
                            <div className="h-11 w-11 rounded-xl bg-brand-soft grid place-items-center">
                                <Mail className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                            </div>
                            <div className="mt-4 text-xs uppercase tracking-wider font-semibold text-slate-500">
                                Careers Email
                            </div>
                            <div className="mt-1 text-slate-800 font-medium">{CAREERS_EMAIL}</div>
                            <a
                                href={`mailto:${CAREERS_EMAIL}?subject=Career%20Enquiry`}
                                className="mt-5"
                                data-testid="car-email-cv-link"
                            >
                                <Button className="w-full rounded-full h-11 bg-brand-navy hover:bg-brand-royal text-white">
                                    Email Your CV <ArrowRight className="h-4 w-4 ml-2" />
                                </Button>
                            </a>
                        </Card>
                    </div>

                    <Card
                        className="mt-5 rounded-2xl border-white bg-white/95 p-5 flex items-start gap-4"
                        data-testid="car-contact-hours"
                    >
                        <div className="h-11 w-11 rounded-xl bg-brand-soft grid place-items-center shrink-0">
                            <Clock className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                        </div>
                        <div className="min-w-0">
                            <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">
                                Office Hours
                            </div>
                            <div className="mt-1 text-slate-800 font-medium leading-snug space-y-0.5 text-sm sm:text-base">
                                {OFFICE_HOURS_LINES.map((l) => (
                                    <div key={l}>{l}</div>
                                ))}
                            </div>
                        </div>
                    </Card>
                </div>
            </section>

            {/* CURRENT OPENINGS --------------------------------------- */}
            <section className="py-20 sm:py-24">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
                    <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-royal bg-brand-soft rounded-full px-3 py-1">
                        Current Openings
                    </span>
                    <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-brand-navy tracking-tight">
                        Openings coming soon
                    </h2>
                    <Card className="mt-8 rounded-2xl border-slate-100 p-10 flex flex-col items-center"
                        data-testid="car-current-openings"
                    >
                        <div className="h-14 w-14 rounded-2xl bg-brand-soft grid place-items-center">
                            <BriefcaseBusiness className="h-6 w-6 text-brand-royal" strokeWidth={1.75} />
                        </div>
                        <p className="mt-5 text-slate-700 leading-relaxed max-w-md">
                            Current openings will be published here.
                        </p>
                        <p className="mt-2 text-xs text-slate-500">
                            In the meantime, please reach out through the channels above.
                        </p>
                    </Card>
                </div>
            </section>

            {/* NOTE (soft) -------------------------------------------- */}
            <section className="pb-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <Card className="rounded-2xl border-brand-soft bg-brand-soft/50 p-6 sm:p-7 flex items-start gap-4"
                        data-testid="car-important-note"
                    >
                        <div className="h-10 w-10 rounded-xl bg-white grid place-items-center shrink-0">
                            <Info className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                        </div>
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wider text-brand-royal">
                                Please note
                            </div>
                            <p className="mt-2 text-slate-700 leading-relaxed">
                                Selection, role details and any offer are subject to approval and
                                applicable requirements.
                            </p>
                        </div>
                    </Card>
                </div>
            </section>

            {/* CTA ---------------------------------------------------- */}
            <section className="bg-gradient-to-br from-brand-navy to-brand-royal text-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
                    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
                        Ready to grow with us?
                    </h2>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <a href={`mailto:${CAREERS_EMAIL}?subject=Career%20Enquiry`} data-testid="car-cta-email">
                            <Button
                                className="bg-white text-brand-navy hover:bg-brand-soft rounded-full h-12 px-6 font-semibold"
                            >
                                Email Your CV <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                        </a>
                        <Button
                            variant="outline"
                            className="rounded-full h-12 px-6 border-white/40 text-white hover:bg-white/10 hover:text-white"
                            onClick={placeholderLoginToast}
                            data-testid="car-cta-customer-login"
                        >
                            Customer Login
                        </Button>
                    </div>
                </div>
            </section>

            {/* FOOTER ------------------------------------------------- */}
            <footer className="bg-brand-navy text-slate-300">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-8">
                    <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-5">
                        <div className="lg:col-span-2">
                            <BrandMark size={40} variant="white" />
                            <p className="mt-5 text-sm leading-relaxed max-w-md text-slate-400">
                                Lokdhara Microfinance is focused on providing structured and accessible
                                financial services through transparent processes, clear records and
                                customer-oriented support.
                            </p>
                            <div className="mt-5 flex gap-3">
                                {[Facebook, Instagram, Linkedin, MessageCircle].map((Icon, i) => (
                                    <a key={i} href="/home"
                                        className="h-9 w-9 rounded-lg bg-white/10 hover:bg-white/20 grid place-items-center transition"
                                        aria-label={`social-${i}`}
                                    >
                                        <Icon className="h-4 w-4" />
                                    </a>
                                ))}
                            </div>
                        </div>
                        {Object.entries(FOOTER_LINKS).map(([col, items]) => (
                            <div key={col}>
                                <div className="text-white font-semibold text-sm mb-4">{col}</div>
                                <ul className="space-y-2 text-sm">
                                    {items.map((item) => (
                                        <li key={item}>
                                            <button
                                                onClick={() => {
                                                    if (item === "About Us") navigate("/about-us");
                                                    else if (["Personal Loan", "Business Loan", "Group Loan"].includes(item)) navigate("/loans");
                                                    else if (item === "Saving Scheme") navigate("/daily-savings");
                                                    else if (item === "Investment") navigate("/investment");
                                                    else if (item === "Careers") navigate("/careers");
                                                    else navigate("/home");
                                                }}
                                                className="hover:text-white transition text-left"
                                            >
                                                {item}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    <p className="mt-12 text-xs text-slate-400 leading-relaxed max-w-3xl">
                        Product availability, eligibility, rates and terms are subject to approval and
                        applicable requirements.
                    </p>

                    <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-400">
                        <div>© {new Date().getFullYear()} Lokdhara Microfinance. All rights reserved.</div>
                        <div className="flex items-center gap-5">
                            <button onClick={() => navigate("/home")} className="hover:text-white">Privacy Policy</button>
                            <button onClick={() => navigate("/home")} className="hover:text-white">Terms &amp; Conditions</button>
                            <Link
                                to="/login"
                                className="text-slate-300 hover:text-white underline underline-offset-4"
                                data-testid="car-footer-staff-login"
                            >
                                Staff Login
                            </Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
